<template>
    <b-card title="" class="trigger-card p-2 pb-4 mb-2" no-body :class="{ 'error-card': !sendTo.length }">
        <template #header class="p-2">
            <div class="d-flex justify-content-between align-items-center w-100">
                <h6 class="text-primary">Send To</h6>
            </div>
        </template>
        <b-card-body class="p-2">
            <b-collapse visible id="send_to">
                <hr class="m-0 mb-3" />

                <b-row class="align-items-center pt-2 mx-lg-5">
                    <b-col>
                        <div class="d-block position-relative">
                            <more-filters-dropdown
                                :icon="['fas', 'chevron-down']"
                                class="send-to-dropdown"
                                v-slot:default="{ handleDropdownShow, handleDropdownHide }"
                                :placeholder="sendTo.length > 0 ? '' : 'Select Recipients'"
                            >
                                <ul class="send-to-list">
                                    <div v-for="key in Object.keys(recipientsOptions)" :key="key">
                                        <li class="send-to-group">{{ getReceiverGroupLabel(key) }}</li>
                                        <div
                                            v-for="option in recipientsOptions[key]"
                                            :key="option.value"
                                            class="send-to-option"
                                            :class="{
                                                'disabled-option': getIsDisabledOption(option.value),
                                            }"
                                        >
                                            <li>
                                                <form-checkbox
                                                    v-model="option.selected"
                                                    :checked="option.selected"
                                                    @change="onChangeSendTo(option.value)"
                                                    class="mb-0"
                                                    :disabled="getIsDisabledOption(option.value)"
                                                    >{{ option.text }}</form-checkbox
                                                >
                                            </li>
                                            <li class="pt-2" v-if="option.value === 2 && sendTo.includes(2)">
                                                <form-search-multiselect
                                                    :value="teamMembers"
                                                    :options="teamMembersMultiselectOptions"
                                                    class="team-members-select"
                                                    placeholder="Select Team Members"
                                                    controlValue="Select Team Member"
                                                    itemLabel="member"
                                                    @input="onChangeTeamMember"
                                                    @show="handleDropdownShow"
                                                    @hide="handleDropdownHide"
                                                    :disabled="isTeamMemberMultiSelectDisabled"
                                                    :applyDefaultSort="false"
                                                ></form-search-multiselect>
                                            </li>
                                        </div>
                                    </div>
                                </ul>
                            </more-filters-dropdown>
                            <div class="d-flex position-absolute selected-items-wrapper">
                                <div
                                    v-for="option in selectedItems"
                                    :key="option.label"
                                    class="mr-2 dropdown-selected-item-wrapper"
                                >
                                    <div
                                        v-if="option.label"
                                        class="d-flex align-items-center dropdown-selected-item text-primary"
                                    >
                                        <span class="d-block mr-2">{{ option.label }}</span>
                                        <span class="d-flex" @click.stop="removeSendTo(option.value)">
                                            <font-awesome-icon class="cursor-pointer" :icon="['fas', 'xmark']" />
                                        </span>
                                    </div>
                                </div>
                                <div
                                    v-if="isMobile ? selectedSendTo.length > 1 : selectedSendTo.length > 3"
                                    class="mr-2 dropdown-selected-item-wrapper"
                                >
                                    <div class="d-flex align-items-center dropdown-selected-item text-primary">
                                        <span class="d-block mr-2"
                                            >+{{ selectedSendTo.length - selectedItems.length }}</span
                                        >
                                    </div>
                                </div>
                            </div>
                        </div>
                    </b-col>
                </b-row>
            </b-collapse>
        </b-card-body>
        <template v-if="!sendTo.length" #footer>
            <small class="text-danger">At least one recipient must be selected</small>
        </template>
    </b-card>
</template>

<script>
    import { mapGetters } from 'vuex';
    import FormDropdown from '../../../generic/form-dropdown/form-dropdown.vue';
    import MoreFiltersDropdown from '../../../generic/more-filters-dropdown/more-filters-dropdown.vue';
    import FormSearchMultiselect from '../../../generic/form-search-multiselect/form-search-multiselect.vue';
    import FormCheckbox from '../../../generic/form-checkbox/form-checkbox.vue';

    const SUBSCRIBER = 'SUBSCRIBER';
    const SUBSCRIBER_TYPE = 1;
    const TEAM_MEMBER_TYPE = 2;
    const ALL_TEAM_MEMBERS_TYPE = 8;

    export default {
        components: {
            FormDropdown,
            MoreFiltersDropdown,
            FormCheckbox,
            FormSearchMultiselect,
        },
        data() {
            return {
                windowWidth: 0,
            };
        },
        computed: {
            ...mapGetters('trigger', [
                'sendTo',
                'sendToOptions',
                'teamMembers',
                'teamMembersOptions',
                'hasRecurringOrDateEvent',
                'isLimited',
            ]),
            teamMembersMultiselectOptions() {
                return [{ value: SUBSCRIBER, text: 'Subscriber' }, ...this.teamMembersOptions];
            },
            sendToWithoutSubscriber() {
                return this.sendTo.filter((item) => item !== SUBSCRIBER_TYPE);
            },
            teamMembersWithoutSubscriber() {
                return this.teamMembers.filter((item) => item !== SUBSCRIBER);
            },
            recipientsOptions() {
                const setSelectedOptions = (options) => {
                    let sendToSelectOptions = options.map((item) => ({
                        ...item,
                        selected: !!this.sendTo.find((i) => i === item.value),
                    }));
                    if (this.hasRecurringOrDateEvent) {
                        sendToSelectOptions = sendToSelectOptions.filter((item) => ![4, 5, 6].includes(item.value));
                    }
                    if (this.isLimited) {
                        sendToSelectOptions = sendToSelectOptions.filter((item) => [2, 6, 8].includes(item.value));
                    }
                    return sendToSelectOptions;
                };
                const options = {
                    dynamic: setSelectedOptions(this.sendToOptions.dynamic),
                    static: setSelectedOptions(this.sendToOptions.static),
                };

                return options;
            },
            isMobile() {
                const md = 768;
                return this.windowWidth < md;
            },
            selectedItems() {
                const selectedItems = this.sendTo.map((item) => {
                    const obj = {
                        value: item,
                        label:
                            this.sendToOptions?.dynamic.find((i) => i.value === item)?.text ||
                            this.sendToOptions?.static.find((i) => i.value === item)?.text,
                    };
                    if (item == TEAM_MEMBER_TYPE) {
                        obj.label = this.teamMembersWithoutSubscriber.length
                            ? `${this.teamMembersWithoutSubscriber.length} team member${
                                  this.teamMembersWithoutSubscriber.length > 1 ? 's' : ''
                              }`
                            : '';
                    }
                    if (item == SUBSCRIBER_TYPE) {
                        obj.label = 'Subscriber';
                    }
                    return obj;
                });
                return selectedItems.filter((item) => item.label).splice(0, this.isMobile ? 1 : 3);
            },
            isTeamMemberMultiSelectDisabled() {
                return this.sendTo.includes(ALL_TEAM_MEMBERS_TYPE);
            },
            isTeamMemberSelectDisabled() {
                return this.sendTo.includes(TEAM_MEMBER_TYPE);
            },
            selectedSendTo() {
                return this.sendTo.filter((item) => {
                    if (item == TEAM_MEMBER_TYPE) {
                        return this.teamMembersWithoutSubscriber.length;
                    }
                    return true;
                });
            },
        },
        mounted() {
            this.onResize();
            window.addEventListener('resize', this.onResize);
        },
        unmounted() {
            window.removeEventListener('resize', this.onResize);
        },
        methods: {
            getIsDisabledOption(value) {
                if (value === TEAM_MEMBER_TYPE) {
                    return this.isTeamMemberMultiSelectDisabled;
                }
                if (value == ALL_TEAM_MEMBERS_TYPE) {
                    return this.isTeamMemberSelectDisabled;
                }
                return false;
            },
            changeDropdownFocus(dropdownRef) {
                this.$refs.send_to_dropdown.closeDropDown();
                this.$refs[dropdownRef].openDropDown();
                this.$nextTick(() => {
                    this.$refs[dropdownRef].$el.scrollIntoView({
                        block: 'center',
                        behavior: 'smooth',
                    });
                });
            },
            onChangeSendTo(value) {
                const sendTo = new Set(this.sendTo);
                if (sendTo.has(value)) {
                    sendTo.delete(value);
                } else {
                    sendTo.add(value);
                }
                if (sendTo.has(ALL_TEAM_MEMBERS_TYPE) && this.teamMembers.length > 0) {
                    sendTo.delete(TEAM_MEMBER_TYPE);
                    this.$store.commit('trigger/setTeamMembers', []);
                }
                if (value === ALL_TEAM_MEMBERS_TYPE && sendTo.has(TEAM_MEMBER_TYPE)) {
                    sendTo.delete(TEAM_MEMBER_TYPE);
                }
                if (value === TEAM_MEMBER_TYPE) {
                    this.$store.commit('trigger/setTeamMembers', []);
                    if (sendTo.has(SUBSCRIBER_TYPE)) {
                        sendTo.delete(SUBSCRIBER_TYPE);
                    }
                }
                this.$store.commit('trigger/setSendTo', [...sendTo]);
                this.$store.commit('trigger/setEditing', true);
            },
            removeSendTo(value) {
                const sendTo = new Set(this.sendTo);
                if (sendTo.size === 1) {
                    sendTo.clear();
                }

                if (value === TEAM_MEMBER_TYPE) {
                    this.$store.commit('trigger/setTeamMembers', [
                        ...this.teamMembers.filter((item) => item === SUBSCRIBER),
                    ]);
                    this.$store.commit(
                        'trigger/setTeamMembersOptions',
                        JSON.parse(JSON.stringify(this.teamMembersOptions))
                    );
                    if (!this.teamMembers.includes(SUBSCRIBER)) {
                        sendTo.delete(TEAM_MEMBER_TYPE);
                    }
                } else if (value === SUBSCRIBER_TYPE) {
                    sendTo.delete(value);
                    if (this.teamMembersWithoutSubscriber.length === 0) {
                        sendTo.delete(TEAM_MEMBER_TYPE);
                    }
                    this.$store.commit('trigger/setTeamMembers', [...this.teamMembersWithoutSubscriber]);
                    this.$store.commit(
                        'trigger/setTeamMembersOptions',
                        JSON.parse(JSON.stringify(this.teamMembersOptions))
                    );
                } else {
                    sendTo.delete(value);
                }
                this.$store.commit('trigger/setSendTo', [...sendTo]);

                this.$store.commit('trigger/setEditing', true);
            },
            updateSendTo(value, type) {
                const sendTo = new Set(this.sendTo);
                if (value.length > 0) {
                    if (!sendTo.has(type)) {
                        sendTo.add(type);
                        this.$store.commit('trigger/setSendTo', [...sendTo]);
                    }
                } else {
                    sendTo.delete(type);
                    this.$store.commit('trigger/setSendTo', [...sendTo]);
                }
                this.$store.commit('trigger/setEditing', true);
            },
            onChangeTeamMember(value) {
                const teamMembers = new Set(this.teamMembers);

                if (value.includes(SUBSCRIBER) && !this.sendTo.includes(1)) {
                    this.updateSendTo(value, SUBSCRIBER_TYPE);
                }
                if (teamMembers.has(SUBSCRIBER) && !value.includes(SUBSCRIBER)) {
                    this.updateSendTo([], SUBSCRIBER_TYPE);
                }
                this.updateSendTo(value, TEAM_MEMBER_TYPE);
                this.$store.commit('trigger/setTeamMembers', value);
                this.$store.commit('trigger/setEditing', true);
                this.$store.commit(
                    'trigger/setTeamMembersOptions',
                    JSON.parse(JSON.stringify(this.teamMembersOptions))
                );
            },
            onResize() {
                this.windowWidth = window.innerWidth;
            },
            getReceiverGroupLabel(option) {
                if (option === 'dynamic') {
                    return 'Dynamic Recipients - Varies by Booking';
                }
                return 'Static Recipients';
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import '../styles.scss';
    .send-to-list {
        padding: 0;
        margin: 0;
        width: 100%;
        li {
            list-style: none;
            width: 100%;
        }
        .disabled-option {
            opacity: 0.7;
        }
        .send-to-option {
            padding: 10px;
            transition: 0.3s;
            &:hover {
                background: #fff;
            }
            :deep(.custom-checkbox label) {
                cursor: pointer;
            }
        }
        .team-members-select {
            :deep(.dropdown-menu) {
                border: none !important;
            }
            :deep(.form-control) {
                padding-left: 0.25rem !important;
            }
        }
    }
    :deep(.send-to-dropdown) {
        border: none !important;
        background: #f1f1f1 !important;
        box-shadow: none !important;
        padding-left: 0.75rem !important;

        & > button.btn {
            position: absolute;
            right: 0;
            background-color: #0d47a1 !important;
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0.5rem 0.75rem;
            height: 100%;
            div {
                width: 0.875em;
                svg {
                    width: 100%;
                }
            }
        }
        label {
            display: block !important;
            box-shadow: none !important;
        }

        & > .dropdown-menu {
            width: 100%;
            transform: none !important;
            top: 35px !important;
            padding: 0 !important;
            background: #f1f1f1 !important;
            div.buttons-wrapper {
                width: 100%;
            }
        }
        &:hover {
            background: #f1f1f1 !important;
        }
    }
    .dropdown-selected-item-wrapper {
        z-index: 2;
    }

    .selected-items-wrapper {
        z-index: 10;
        position: absolute;
        top: 0;
        left: 0;
    }

    .dropdown-selected-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 5px;
        background: rgba(13, 71, 161, 0.1);
        border: 1px solid #0d47a1;
        border-radius: 6px;
        @media (max-width: 768px) {
            span {
                max-width: 160px;
                overflow: hidden;
                white-space: nowrap;
                text-overflow: ellipsis;
            }
        }
    }

    .send-to-group {
        padding: 10px;
        font-weight: bold;
        border-bottom: 1px solid #cdcdcd;
        display: block;
        width: 100%;
        font-size: 15px;
    }
</style>
